import { KeyRound, Loader2, ShieldCheck, ShieldOff } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { renderSVG } from 'uqr';
import { NewPasswordField } from '../components/sign-in';
import { Card, Field, btnPrimary, btnSecondary, inputCls } from '../components/ui';
import {
  accountSecurity,
  changePassword,
  cognitoMessage,
  confirmTotpSetup,
  otpauthUri,
  setTotp,
  startTotpSetup,
} from '../lib/cognito';
import { useSession } from '../lib/session';

const ISSUER = 'Salento Sagre Eventi';

/**
 * Il mio account (docs/07-admin-e-bot.md §1): autenticazione a due fattori con un'app di
 * autenticazione (TOTP: Google Authenticator, Microsoft Authenticator, 1Password…) e cambio della
 * password. Tutto direttamente su Cognito con l'access token della sessione.
 */
export function Account() {
  const { config, me, isAdmin, accessToken } = useSession();
  const [totp, setTotpState] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    accessToken()
      .then((t) => accountSecurity(config, t))
      .then(
        (s) => setTotpState(s.totp),
        (e) => setError(cognitoMessage(e)),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main className="mx-auto max-w-[760px] p-4 md:p-6">
      <h1 className="font-display text-h2">Il mio account</h1>
      <p className="mt-1 text-sm text-muted">
        {me?.email ?? '…'}
        {me && ` · ${me.groups.join(', ') || 'nessun ruolo'}`}
      </p>
      {error && (
        <p role="alert" className="mt-4 bg-danger-soft p-3 text-sm">
          {error}
        </p>
      )}
      <div className="mt-4 flex flex-col gap-6">
        <TwoFactor enabled={totp} isAdmin={isAdmin} onChange={setTotpState} />
        <PasswordChange />
      </div>
    </main>
  );
}

function TwoFactor({
  enabled,
  isAdmin,
  onChange,
}: {
  enabled: boolean | null;
  isAdmin: boolean;
  onChange: (v: boolean) => void;
}) {
  const { config, me, accessToken } = useSession();
  const [secret, setSecret] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
    } catch (e) {
      setError(cognitoMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const start = () =>
    run(async () => {
      setCode('');
      setSecret(await startTotpSetup(config, await accessToken()));
    });
  const confirm = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      await confirmTotpSetup(config, await accessToken(), code.trim());
      setSecret(null);
      onChange(true);
      setNotice(
        'Autenticazione a due fattori attiva: dal prossimo accesso ti chiediamo il codice.',
      );
    });
  };
  const disable = () => {
    if (
      !window.confirm(
        isAdmin
          ? 'Sei un amministratore: senza il secondo fattore il pannello è meno protetto. Disattivare comunque?'
          : 'Disattivare l’autenticazione a due fattori?',
      )
    )
      return;
    void run(async () => {
      await setTotp(config, await accessToken(), false);
      onChange(false);
      setNotice('Autenticazione a due fattori disattivata.');
    });
  };

  const qr = secret
    ? `data:image/svg+xml;utf8,${encodeURIComponent(
        renderSVG(otpauthUri(secret, me?.email ?? 'pannello', ISSUER), { border: 2 }),
      )}`
    : null;

  return (
    <Card title="Autenticazione a due fattori">
      {enabled === null ? (
        <p className="text-sm text-muted">Caricamento…</p>
      ) : (
        <>
          <p className="flex items-center gap-2 text-sm">
            {enabled ? (
              <>
                <ShieldCheck className="size-5 shrink-0 text-ok" aria-hidden />
                <span>
                  <b>Attiva.</b> A ogni accesso, dopo la password, ti chiediamo il codice dell’app
                  di autenticazione.
                </span>
              </>
            ) : (
              <>
                <ShieldOff className="size-5 shrink-0 text-muted" aria-hidden />
                <span>
                  <b>Non attiva.</b> Oltre alla password, chiede un codice che cambia ogni 30
                  secondi nell’app di autenticazione del telefono (Google Authenticator, Microsoft
                  Authenticator, 1Password…).
                  {isAdmin && ' Per gli amministratori è caldamente consigliata.'}
                </span>
              </>
            )}
          </p>
          {notice && (
            <p role="status" className="bg-ok-soft p-3 text-sm">
              {notice}
            </p>
          )}
          {error && (
            <p role="alert" className="bg-danger-soft p-3 text-sm">
              {error}
            </p>
          )}
          {!enabled && !secret && (
            <div>
              <button onClick={() => void start()} disabled={busy} className={btnPrimary}>
                {busy ? (
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                ) : (
                  <ShieldCheck className="size-4" aria-hidden />
                )}
                Attiva
              </button>
            </div>
          )}
          {secret && qr && (
            <form onSubmit={confirm} className="grid gap-4 md:grid-cols-[200px_1fr]">
              <img
                src={qr}
                alt="Codice QR da inquadrare con l’app di autenticazione"
                className="size-[200px] border border-surface-strong bg-page"
              />
              <div className="flex flex-col gap-3 text-sm">
                <ol className="list-decimal pl-5">
                  <li>Apri l’app di autenticazione e aggiungi un account.</li>
                  <li>Inquadra il codice QR, oppure inserisci a mano la chiave qui sotto.</li>
                  <li>Scrivi il codice a 6 cifre che compare nell’app.</li>
                </ol>
                <p>
                  Chiave:{' '}
                  <code className="select-all break-all bg-surface px-1 font-mono">
                    {secret.match(/.{1,4}/g)?.join(' ')}
                  </code>
                </p>
                <Field label="Codice a 6 cifre">
                  <input
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    required
                    autoFocus
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    className={`${inputCls} max-w-[200px] text-center font-mono text-lg tracking-[0.4em]`}
                  />
                </Field>
                <div className="flex gap-2">
                  <button type="submit" disabled={busy || code.length !== 6} className={btnPrimary}>
                    {busy && <Loader2 className="size-4 animate-spin" aria-hidden />}
                    Verifica e attiva
                  </button>
                  <button type="button" onClick={() => setSecret(null)} className={btnSecondary}>
                    Annulla
                  </button>
                </div>
              </div>
            </form>
          )}
          {enabled && (
            <div className="flex flex-wrap gap-2">
              <button onClick={() => void start()} disabled={busy} className={btnSecondary}>
                Cambia telefono
              </button>
              <button onClick={disable} disabled={busy} className={btnSecondary}>
                Disattiva
              </button>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

function PasswordChange() {
  const { config, accessToken } = useSession();
  const [previous, setPrevious] = useState('');
  const [proposed, setProposed] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const mismatch = again.length > 0 && again !== proposed;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (mismatch) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await changePassword(config, await accessToken(), previous, proposed);
      setPrevious('');
      setProposed('');
      setAgain('');
      setNotice('Password cambiata.');
    } catch (err) {
      setError(cognitoMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Password">
      <form onSubmit={(e) => void submit(e)} className="flex max-w-[420px] flex-col gap-4">
        <Field label="Password attuale">
          <input
            type="password"
            autoComplete="current-password"
            required
            value={previous}
            onChange={(e) => setPrevious(e.target.value)}
            className={inputCls}
          />
        </Field>
        <NewPasswordField value={proposed} onChange={setProposed} />
        <Field label="Ripeti la password nuova" error={mismatch ? 'Non coincide' : undefined}>
          <input
            type="password"
            autoComplete="new-password"
            required
            value={again}
            onChange={(e) => setAgain(e.target.value)}
            className={inputCls}
          />
        </Field>
        {notice && (
          <p role="status" className="bg-ok-soft p-3 text-sm">
            {notice}
          </p>
        )}
        {error && (
          <p role="alert" className="bg-danger-soft p-3 text-sm">
            {error}
          </p>
        )}
        <div>
          <button type="submit" disabled={busy || mismatch} className={btnPrimary}>
            {busy ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <KeyRound className="size-4" aria-hidden />
            )}
            Cambia la password
          </button>
        </div>
      </form>
    </Card>
  );
}
